import { useMemo } from 'react';
import { ArrowDownUp, Search, X } from 'lucide-react';
import { Input } from './input';
import { Select } from './select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { cn } from '@/lib/cn';

/** The business opened in 2026 — no ledger data predates it. */
const BUSINESS_START_YEAR = 2026;

/** 0 = the whole year; 1-4 = calendar quarters. */
export type Quarter = 0 | 1 | 2 | 3 | 4;
export type Period = { year: number; quarter: Quarter };

/** Descending list of selectable years, current year back to the business start. */
export function availableYears(): number[] {
  const top = Math.max(new Date().getFullYear(), BUSINESS_START_YEAR);
  const years: number[] = [];
  for (let y = top; y >= BUSINESS_START_YEAR; y--) {
    years.push(y);
  }
  return years;
}

export function currentPeriod(): Period {
  return { year: Math.max(new Date().getFullYear(), BUSINESS_START_YEAR), quarter: 0 };
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * ISO `from`/`to` bounds for a period. Quarter 0 spans the whole year;
 * quarters are calendar quarters (Q1 = Jan-Mar … Q4 = Oct-Dec).
 */
export function periodRange(p: Period): { from: string; to: string } {
  if (p.quarter === 0) {
    return { from: `${p.year}-01-01`, to: `${p.year}-12-31` };
  }
  const startMonth = (p.quarter - 1) * 3 + 1;
  const endMonth = startMonth + 2;
  const lastDay = new Date(p.year, endMonth, 0).getDate();
  return {
    from: `${p.year}-${pad(startMonth)}-01`,
    to: `${p.year}-${pad(endMonth)}-${pad(lastDay)}`,
  };
}

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="relative min-w-0 flex-1 sm:max-w-xs">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? 'Search…'}
        className="h-9 pl-9 pr-8"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

const QUARTERS: { q: Quarter; label: string }[] = [
  { q: 0, label: 'All' },
  { q: 1, label: 'Q1' },
  { q: 2, label: 'Q2' },
  { q: 3, label: 'Q3' },
  { q: 4, label: 'Q4' },
];

export function PeriodFilter({
  value,
  onChange,
}: {
  value: Period;
  onChange: (p: Period) => void;
}) {
  const years = useMemo(availableYears, []);
  return (
    <div className="flex items-center gap-1.5">
      <Select
        value={value.year}
        onChange={(e) => onChange({ ...value, year: Number(e.target.value) })}
        aria-label="Year"
        className="h-9 w-auto"
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </Select>
      <div className="inline-flex items-center gap-0.5 rounded-md border border-[var(--color-border)] bg-card p-0.5">
        {QUARTERS.map(({ q, label }) => (
          <button
            key={q}
            type="button"
            onClick={() => onChange({ ...value, quarter: q })}
            className={cn(
              'rounded px-2.5 py-1 text-xs font-medium transition-colors',
              value.quarter === q
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export type SortOption = { value: string; label: string };

export function SortMenu({
  options,
  value,
  onChange,
}: {
  options: SortOption[];
  value: string;
  onChange: (v: string) => void;
}) {
  const active = options.find((o) => o.value === value) ?? options[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-card px-3 text-sm text-foreground transition-colors hover:bg-muted"
        aria-label="Sort"
      >
        <ArrowDownUp className="size-3.5 text-muted-foreground" />
        {active.label}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {options.map((o) => (
          <DropdownMenuItem key={o.value} onClick={() => onChange(o.value)}>
            {o.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A consistent filter-bar wrapper: search on the left, period + sort on the right. */
export function LedgerToolbar({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      {children}
    </div>
  );
}
